import compact from 'lodash/compact';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import some from 'lodash/some';

import {
  IAutoPolicy,
  TDriverRelationship,
  IPolicyDriver,
  IPolicyVehicle,
} from '../../../interfaces';

import { TPolicyHealthCheckErrorState } from './usePolicyHealthCheck';

export type TGetPolicyHealthCheckProps = {
  errorDetails: TPolicyHealthCheckErrorState;
  stagedPolicy: IAutoPolicy;
};

export type TPolicyDriverMissingLicenseInfo = {
  driverNumber: string | number;
  driving_license_state: string;
  driving_license_number: string;
};

export type TUpdateHealthCheckDriver = IPolicyDriver & {
  driverNumber: number | string;
};

export type TUpdatedHealthCheckVins = Record<string, string>;

export type THealthCheckExcludedDriver = {
  id?: number | string;
  driving_license_state: string;
  driving_license_number: string;
  relationship_to_insured: TDriverRelationship;
};

export function getPolicyHealthCheckProps({
  errorDetails,
  stagedPolicy,
}: TGetPolicyHealthCheckProps) {
  const activeDriversList = stagedPolicy.details.drivers.filter(
    (driver) => !driver.deleted && !driver.is_excluded
  );

  const activeVehiclesList = stagedPolicy.details.vehicles.filter(
    (vehicle) => !vehicle.deleted
  );

  const driversWithDriverNumber: TUpdateHealthCheckDriver[] =
    activeDriversList.map((driver, i) => ({
      ...driver,
      driverNumber: i + 1,
    }));

  const unverifiableVins = errorDetails?.unverifiableVehicleVins || [];

  const invalidDrivingLicenseNumbers =
    errorDetails?.unverifiableDriversLicenses?.map(
      ({ driving_license_number }) => driving_license_number
    );
  const missingLicenseDriverList: TUpdateHealthCheckDriver[] =
    driversWithDriverNumber.filter((driver) =>
      invalidDrivingLicenseNumbers.includes(driver.driving_license_number!)
    );

  const missingVinVehicleList = activeVehiclesList.filter((vehicle) =>
    unverifiableVins.includes(vehicle.vin)
  );

  const vehicleVinBlacklist = activeVehiclesList
    .map((vehicle) => vehicle.vin)
    .filter((vin) => !!vin);

  const ineligibleVins = errorDetails?.ineligibleVehicleVins || [];
  const vehiclesToExclude = activeVehiclesList.filter((vehicle) =>
    ineligibleVins.includes(vehicle.vin)
  );

  const driversToExclude = [
    ...compact(
      errorDetails?.ineligibleDriversLicenses?.map((licenseDetails) =>
        activeDriversList.find(
          (driver) =>
            licenseDetails.driving_license_number ===
              driver.driving_license_number &&
            licenseDetails.driving_license_state ===
              driver.driving_license_state
        )
      )
    ),
    ...compact(
      errorDetails?.ineligibleDriverIds?.map((id) =>
        activeDriversList.find(({ id: activeId }) => activeId === id)
      )
    ),
    ...compact(errorDetails?.ineligibleDrivers),
  ];

  function updateLicenseInfo(
    updatedDriverValues: TPolicyDriverMissingLicenseInfo[]
  ) {
    const updatedDrivers = driversWithDriverNumber.map((driver) => {
      const updatedValuesForDriver = updatedDriverValues.find(
        ({ driverNumber }) => driverNumber === driver.driverNumber
      );
      return updatedValuesForDriver
        ? { ...driver, ...updatedValuesForDriver }
        : driver;
    });
    const updatedDriversWithoutDriverNumber = updatedDrivers.map((driver) =>
      omit(driver, ['driverNumber'])
    );

    return {
      ...stagedPolicy,
      details: {
        ...stagedPolicy.details,
        drivers: updatedDriversWithoutDriverNumber,
      },
    };
  }

  function updateVehicleVins(updatedVins: TUpdatedHealthCheckVins) {
    const updatedVehicles = stagedPolicy.details.vehicles.map((vehicle) => {
      if (includes(Object.keys(updatedVins), vehicle.vin)) {
        return {
          ...vehicle,
          vin: updatedVins[vehicle.vin],
        };
      }
      return vehicle;
    });

    return {
      ...stagedPolicy,
      details: {
        ...stagedPolicy.details,
        vehicles: updatedVehicles,
      },
    };
  }

  function excludeVehicles(excludedVehicles: IPolicyVehicle[]) {
    const excludedVehicleIds = excludedVehicles.map(
      (vehicle) => vehicle.id || vehicle.vin
    );
    const newVehiclesList = stagedPolicy.details.vehicles.filter(
      (vehicle) => !excludedVehicleIds.includes(vehicle.id || vehicle.vin)
    );

    return {
      ...stagedPolicy,
      details: {
        ...stagedPolicy.details,
        vehicles: newVehiclesList,
      },
    };
  }

  function excludeDrivers(excludedDrivers: THealthCheckExcludedDriver[]) {
    const newDriversList = stagedPolicy.details.drivers.filter(
      (driver) =>
        !some(
          excludedDrivers,
          (excludedDriverInfo) =>
            (!!excludedDriverInfo.id && excludedDriverInfo.id === driver.id) ||
            (excludedDriverInfo.driving_license_state ===
              driver.driving_license_state &&
              excludedDriverInfo.driving_license_number ===
                driver.driving_license_number)
        )
    );

    const updatedPolicyHoldersList = stagedPolicy.policy_holders.filter(
      (policyholder) =>
        !some(
          excludedDrivers,
          (excludedDriverInfo) =>
            excludedDriverInfo.relationship_to_insured === 'spouse' &&
            policyholder.position_on_policy === 2
        )
    );
    return {
      ...stagedPolicy,
      details: {
        ...stagedPolicy.details,
        drivers: newDriversList,
      },
      policy_holders: updatedPolicyHoldersList,
    };
  }

  return {
    missingLicenseDriverList,
    activeDriversList,
    vehicleVinBlacklist,
    missingVinVehicleList,
    vehiclesToExclude,
    driversToExclude,
    updateLicenseInfo,
    updateVehicleVins,
    excludeVehicles,
    excludeDrivers,
  };
}
